import { SopForm } from 'components/sop/sop-form/SopForm'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { API_SOP } from 'services/apiSop'
import { useFetch } from 'services/useFetch'
import { capitalize, constructOptionsSelect, uploadFileToS3 } from 'utils/utils'
import { Alert } from 'components/commons/alert'

const INITIAL_FORM_VALUE = {
    name: '',
    number: '',
    category: null,
    employees: [],
    employee_types: [],
}

const INITIAL_LINK = { name: '', url: '' }

const isValidUrl = (urlStr) => {
    if (!urlStr || typeof urlStr !== 'string') return false
    const trimmed = urlStr.trim()
    if (/\s/.test(trimmed)) return false
    const formatted = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
    try {
        const parsed = new URL(formatted)
        return Boolean(
            (parsed.protocol === 'http:' || parsed.protocol === 'https:') &&
            parsed.hostname &&
            parsed.hostname.includes('.') &&
            !parsed.hostname.startsWith('.') &&
            !parsed.hostname.endsWith('.')
        )
    } catch {
        return false
    }
}

export const SopFormContainer = () => {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const sopId = searchParams.get('sopId') || searchParams.get('id')
    const isEdit = Boolean(sopId)

    const [formValue, setFormValue] = useState(INITIAL_FORM_VALUE)
    const [file, setFile] = useState(null)
    const [additionalLinks, setAdditionalLinks] = useState([{ ...INITIAL_LINK }])
    const [categoryOptions, setCategoryOptions] = useState([])
    const [employeeOptions, setEmployeeOptions] = useState([])
    const [employeeTypeOptions, setEmployeeTypeOptions] = useState([])
    const [errorForm, setErrorForm] = useState({})
    const [isLoadingUpload, setIsLoadingUpload] = useState(false)
    const [confirmModal, setConfirmModal] = useState({
        show: false,
        action: null,
        text: '',
        confirmText: '',
    })

    const { fetching: getCategoryList } = useFetch(API_SOP.CATEGORY_LIST)
    const { fetching: getEmployeeList } = useFetch(API_SOP.EMPLOYEE_LIST)
    const { fetching: getEmployeeTypeList } = useFetch(API_SOP.EMPLOYEE_TYPE_LIST)
    const { fetching: createSop, isLoading: isCreating } = useFetch(API_SOP.CREATE)
    const { fetching: updateSop, isLoading: isUpdating } = useFetch(API_SOP.UPDATE)
    const { fetching: getSopDetail } = useFetch(API_SOP.DETAIL)
    const { fetching: getUploadUrl } = useFetch(API_SOP.UPLOAD_URL)

    useEffect(() => {
        fetchCategoryOptions()
        fetchEmployeeOptions()
        fetchEmployeeTypeOptions()
    }, [])

    useEffect(() => {
        if (sopId) {
            getSopDetail({}, sopId).then((response) => {
                const data = response?.data
                if (data) {
                    setFormValue({
                        name: data.name || '',
                        number: data.number || '',
                        category: data.category?.id ? { value: data.category.id, label: data.category.name } : null,
                        employees:
                            data.employees?.map((emp) => ({
                                value: emp.id,
                                label: emp.name || emp.full_name || emp.number || emp.id,
                            })) || [],
                        employee_types:
                            data.employee_types?.map((type) => ({
                                value: type,
                                label: capitalize(type)?.replace(/-/g, ' ') || type,
                            })) || [],
                    })
                    if (data.document_url) {
                        setFile(data.document_url)
                    }
                    if (data.additional_links?.length > 0) {
                        setAdditionalLinks(
                            data.additional_links.map((link) => ({
                                name: link.name || '',
                                url: link.url || '',
                            })),
                        )
                    } else {
                        setAdditionalLinks([{ ...INITIAL_LINK }])
                    }
                }
            })
        }
    }, [sopId])

    const fetchCategoryOptions = async (search = '') => {
        try {
            const response = await getCategoryList({ search })
            const rawData = response?.data?.results || response?.data?.result || response?.data || []
            const options = constructOptionsSelect(rawData, 'name', 'id')
            setCategoryOptions(options)
        } catch (error) {
            setCategoryOptions([])
        }
    }

    const fetchEmployeeOptions = async (search = '') => {
        try {
            const response = await getEmployeeList({ search })
            const rawData = response?.data?.results || response?.data?.result || response?.data || []
            const options = Array.isArray(rawData)
                ? rawData.map((emp) => ({
                      label: emp.full_name || emp.name || emp.number || emp.id,
                      value: emp.id,
                  }))
                : []
            setEmployeeOptions(options)
        } catch (error) {
            setEmployeeOptions([])
        }
    }

    const fetchEmployeeTypeOptions = async (search = '') => {
        try {
            const response = await getEmployeeTypeList({ search })
            const rawData = response?.data?.results || response?.data?.result || response?.data || []
            const options = Array.isArray(rawData)
                ? rawData.map((item) => {
                      if (typeof item === 'string') {
                          return {
                              value: item,
                              label: capitalize(item)?.replace(/-/g, ' '),
                          }
                      }
                      return {
                          value: item?.id ?? item?.value,
                          label: item?.label ?? item?.name ?? item?.id,
                      }
                  })
                : []
            setEmployeeTypeOptions(options)
        } catch (error) {
            setEmployeeTypeOptions([])
        }
    }

    const onSearchCategory = (keyword) => fetchCategoryOptions(keyword)
    const onSearchEmployee = (keyword) => fetchEmployeeOptions(keyword)
    const onSearchEmployeeType = (keyword) => fetchEmployeeTypeOptions(keyword)

    const onChangeValue = ({ name, value }) => {
        setFormValue((prev) => ({ ...prev, [name]: value }))
        setErrorForm((prev) => ({ ...prev, [name]: undefined }))
    }

    const onUploadFile = (selectedFile) => {
        setFile(selectedFile)
        setErrorForm((prev) => ({ ...prev, file: undefined }))
    }

    const onDeleteFile = () => {
        setFile(null)
    }

    const onAddLink = () => {
        setAdditionalLinks((prev) => [...prev, { ...INITIAL_LINK }])
    }

    const onRemoveLink = (index) => {
        setAdditionalLinks((prev) => prev.filter((_, i) => i !== index))
        setErrorForm((prev) => {
            const updated = { ...prev }
            delete updated[`link_name_${index}`]
            delete updated[`link_url_${index}`]
            return updated
        })
    }

    const onChangeLinkValue = (index, field, value) => {
        setAdditionalLinks((prev) => {
            const updated = [...prev]
            updated[index] = { ...updated[index], [field]: value }
            return updated
        })
        setErrorForm((prev) => ({
            ...prev,
            [`link_${field}_${index}`]: undefined,
        }))
    }

    const validateDraft = () => {
        const errors = {}

        if (!formValue.name || !formValue.name.trim()) {
            errors.name = 'SOP Name tidak boleh kosong'
        }

        additionalLinks.forEach((link, index) => {
            const hasName = Boolean(link?.name && link?.name.trim())
            const hasUrl = Boolean(link?.url && link?.url.trim())

            if (hasName && !hasUrl) {
                errors[`link_url_${index}`] = 'URL link tidak boleh kosong'
                console.log(`Link ke - ${index}: URL link tidak boleh kosong`)
            } else if (hasUrl && !isValidUrl(link.url)) {
                errors[`link_url_${index}`] = 'URL tidak valid'
                console.log(`Link ke - ${index}: URL tidak valid`)
            }
        })

        setErrorForm(errors)
        return Object.keys(errors).length === 0
    }

    const validateSubmit = () => {
        const errors = {}

        if (!formValue.name || !formValue.name.trim()) {
            errors.name = 'SOP Name tidak boleh kosong'
        }
        if (!formValue.number || !formValue.number.trim()) {
            errors.number = 'SOP Number tidak boleh kosong'
        }
        if (!formValue.category?.value) {
            errors.category = 'SOP Category tidak boleh kosong'
        }
        if (!file) {
            errors.file = 'File PDF wajib diupload'
        }

        additionalLinks.forEach((link, index) => {
            const hasName = Boolean(link?.name && link?.name.trim())
            const hasUrl = Boolean(link?.url && link?.url.trim())

            if (hasName || hasUrl) {
                if (!hasName) {
                    errors[`link_name_${index}`] = 'Nama link tidak boleh kosong'
                }
                if (!hasUrl) {
                    errors[`link_url_${index}`] = 'URL link tidak boleh kosong'
                } else if (!isValidUrl(link.url)) {
                    errors[`link_url_${index}`] = 'URL tidak valid'
                }
            }
        })

        setErrorForm(errors)
        return Object.keys(errors).length === 0
    }

    const uploadFileToServer = async () => {
        if (!file) return null
        if (typeof file === 'string') return file

        setIsLoadingUpload(true)
        try {
            const fileName = file?.name?.replace(/\s/g, '')
            const s3Response = await getUploadUrl({ file: fileName })
            const s3Data = s3Response?.data
            if (s3Data) {
                await uploadFileToS3(s3Data, file)
                return `${s3Data.url}${s3Data.fields.key}`
            }
            return null
        } catch (err) {
            Alert({
                icon: 'error',
                title: 'Error',
                message: 'Upload File Failed',
            })
            return null
        } finally {
            setIsLoadingUpload(false)
        }
    }

    const executeSave = async (status) => {
        let documentUrl = null
        if (file) {
            documentUrl = await uploadFileToServer()
            if (!documentUrl && status === 'active') return
        }

        const validLinks = additionalLinks
            .filter((link) => link?.name?.trim() && link?.url?.trim())
            .map((link) => ({
                name: link.name.trim(),
                url: link.url.trim(),
            }))

        const payload = {
            status,
            name: formValue.name?.trim(),
            number: formValue.number?.trim() || (status === 'draft' ? '' : undefined),
            sop_category_id: formValue.category?.value || (status === 'draft' ? null : undefined),
            document_url: documentUrl || (status === 'draft' ? '' : undefined),
            additional_links: validLinks,
            employee_ids: formValue.employees?.map?.((emp) => emp?.value) || [],
            employee_types: formValue.employee_types?.map?.((type) => type?.value) || [],
        }

        const actionPromise = isEdit ? updateSop(payload, sopId) : createSop(payload)

        actionPromise.then(() => {
            const successMessage =
                status === 'draft'
                    ? isEdit
                        ? 'SOP berhasil diperbarui sebagai Draft'
                        : 'SOP berhasil disimpan sebagai Draft'
                    : isEdit
                      ? 'SOP berhasil disubmit'
                      : 'SOP berhasil disubmit'

            Alert({
                icon: 'success',
                title: 'Success',
                message: successMessage,
            })
            navigate('/sop-settings/sop-list')
        })
    }

    const onSaveDraft = () => {
        if (!validateDraft()) return
        setConfirmModal({
            show: true,
            action: 'draft',
            text: isEdit
                ? 'Apakah anda yakin ingin menyimpan perubahan SOP ini sebagai draft?'
                : 'Apakah anda yakin ingin menyimpan SOP ini sebagai draft?',
            confirmText: 'Simpan Draft',
        })
    }

    const onSubmitForm = () => {
        if (!validateSubmit()) return
        setConfirmModal({
            show: true,
            action: 'active',
            text: isEdit
                ? 'Apakah anda yakin ingin mensubmit perubahan SOP ini?'
                : 'Apakah anda yakin ingin mensubmit SOP ini?',
            confirmText: 'Submit',
        })
    }

    const onConfirmAction = () => {
        const action = confirmModal.action
        setConfirmModal((prev) => ({ ...prev, show: false }))
        executeSave(action)
    }

    const onCloseConfirmModal = () => {
        setConfirmModal((prev) => ({ ...prev, show: false }))
    }

    const isSaving = isCreating || isUpdating || isLoadingUpload

    const props = {
        isEdit,
        formValue,
        onChangeValue,
        categoryOptions,
        onSearchCategory,
        employeeOptions,
        employeeTypeOptions,
        onSearchEmployee,
        onSearchEmployeeType,
        file,
        onUploadFile,
        onDeleteFile,
        additionalLinks,
        onAddLink,
        onRemoveLink,
        onChangeLinkValue,
        onSaveDraft,
        onSubmitForm,
        isLoadingSave: isSaving,
        isLoadingSubmit: isSaving,
        errorForm,
        confirmModal,
        onCloseConfirmModal,
        onConfirmAction,
    }

    return <SopForm {...props} />
}
